import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { genererSeances, couleurRPE, type SeanceGeneree } from './generateurEngine.ts';
import { useVma, saveLastVma } from '../Allures/useVma.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { ZoneChip, LegendeZones } from '../Allures/couleursZones.tsx';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { listerSeances, type Seance } from '../Construction/seancesApi.ts';
import { empreinteNotation, nomDoublon } from '../Construction/doublons.ts';
import { categorieDe } from '../Construction/composition.ts';

const TOUTES_LES_ZONES = 'TOUTES';

// Meme tableau triable que la page Allures (@mui/x-data-grid), plutot
// qu'un <Table> MUI classique.
type Ligne = SeanceGeneree & { id: number; notationSeance: string; existantes: Seance[] };

/**
 * Notation de la seance generee au format de la page Seances : le contenu
 * repete entre parentheses ("6x1000m@10K-R2'" -> "6x(1000m@10K-R2')").
 */
export function notationPourSeance(notation: string): string {
  const m = notation.match(/^(\d+)\s*x\s*(?!\()(.+)$/i);
  return m ? `${m[1]}x(${m[2]})` : notation;
}

const colonnesBase: GridColDef<Ligne>[] = [
  { field: 'nomZone', headerName: 'Allure', width: 170, renderCell: (params) => <ZoneChip nom={params.value} /> },
  {
    field: 'notation',
    headerName: 'Séance',
    width: 220,
    renderCell: (params) => <code>{params.row.notationSeance}</code>,
  },
  { field: 'nbReps', headerName: 'Nb reps', type: 'number', width: 100 },
  {
    field: 'distanceM',
    headerName: 'Distance / rep.',
    type: 'number',
    width: 150,
    valueFormatter: (value: number) => `${value} m`,
  },
  {
    field: 'effortS',
    headerName: 'Temps / rep.',
    type: 'number',
    width: 130,
    renderCell: (params) => params.row.effortFormatted,
  },
  {
    field: 'recuperationS',
    headerName: 'Récup.',
    type: 'number',
    width: 110,
    renderCell: (params) => params.row.recuperationFormatted,
  },
  {
    field: 'tempsTotalS',
    headerName: 'Temps total',
    type: 'number',
    width: 130,
    renderCell: (params) => params.row.tempsTotalFormatted,
  },
  {
    field: 'distanceTotaleM',
    headerName: 'Distance totale',
    type: 'number',
    width: 150,
    valueFormatter: (value: number) => `${value} m`,
  },
  {
    field: 'rpe',
    headerName: 'RPE',
    type: 'number',
    width: 90,
    renderCell: (params) => {
      const couleur = couleurRPE(params.value as number);
      return (
        <div
          style={{
            backgroundColor: couleur.background,
            color: couleur.color,
            fontWeight: 'bold',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {params.value}
        </div>
      );
    },
  },
];

export default function Modeles() {
  // Meme VMA de reference que Construction (preference utilisateur,
  // equivalent de "=Allures!C5" dans le classeur d'origine) - voir
  // src/Allures/useVma.ts.
  const [vma, setVma] = useVma();
  const [zoneFiltre, setZoneFiltre] = React.useState(TOUTES_LES_ZONES);

  // Parametres d'allures de l'utilisateur (ou valeurs par defaut).
  const { table } = useAllureTable();

  const seances = React.useMemo<SeanceGeneree[]>(() => genererSeances(vma, table), [vma, table]);

  const zones = React.useMemo(() => {
    const noms: string[] = [];
    seances.forEach((s) => {
      if (noms.indexOf(s.nomZone) === -1) noms.push(s.nomZone);
    });
    return noms;
  }, [seances]);

  const navigate = useNavigate();

  // Seances deja enregistrees (page Seances, socle commun compris), pour signaler les doublons.
  const [enregistrees, setEnregistrees] = React.useState<Seance[]>([]);
  React.useEffect(() => {
    listerSeances()
      .then(setEnregistrees)
      .catch(() => setEnregistrees([]));
  }, []);
  const parEmpreinte = React.useMemo(() => {
    const index = new Map<string, Seance[]>();
    enregistrees.forEach((s) => {
      if (categorieDe(s) === 'MODELE') return;
      const cle = empreinteNotation(s.notation, vma, table);
      if (cle) index.set(cle, [...(index.get(cle) ?? []), s]);
    });
    return index;
  }, [enregistrees, vma, table]);

  const seancesAffichees = zoneFiltre === TOUTES_LES_ZONES ? seances : seances.filter((s) => s.nomZone === zoneFiltre);
  const lignes = React.useMemo<Ligne[]>(
    () =>
      seancesAffichees.map((s, index) => {
        const notationSeance = notationPourSeance(s.notation);
        const cle = empreinteNotation(notationSeance, vma, table);
        return { id: index, ...s, notationSeance, existantes: (cle && parEmpreinte.get(cle)) || [] };
      }),
    [seancesAffichees, parEmpreinte, vma, table],
  );

  // Envoie la seance generee dans l'editeur de la page Seances, pre-remplie.
  const creer = (l: Ligne) =>
    navigate('/seances/nouvelle', {
      state: { notation: l.notationSeance, nom: `${l.nbReps}x${l.distanceM} m — ${l.nomZone}`, categorie: 'CORPS' },
    });

  const columns: GridColDef<Ligne>[] = [
    {
      field: 'creer',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Ouvrir cette séance dans l'éditeur pour la modifier et l'enregistrer">
          <Button size="small" startIcon={<EditNoteIcon />} onClick={() => creer(params.row)}>
            Créer
          </Button>
        </Tooltip>
      ),
    },
    ...colonnesBase,
    {
      field: 'existantes',
      headerName: 'Mes séances',
      width: 170,
      sortable: false,
      renderCell: (params) =>
        params.row.existantes.length > 0 ? (
          <Tooltip title={`Déjà enregistrée : ${params.row.existantes.map(nomDoublon).join(', ')}`}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Déjà enregistrée"
              onClick={() => navigate(`/seances/${params.row.existantes[0].id}`)}
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Box component="main" sx={{ p: 12, pt: 10 }}>
      <Button component={Link} to="/seances/nouvelle" startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}>
        Nouvelle séance
      </Button>

      <Typography sx={{ mb: 2 }}>
        Génère automatiquement toutes les séances de répétitions (
        <code>Nx(&lt;distance&gt;m@&lt;allure&gt;-R&lt;récup&gt;)</code>) compatibles avec ta VMA et les
        contraintes usuelles (nombre de répétitions, volume, distance) de chaque zone d'allure — portage de
        la fonction <code>genererSeances</code> du générateur de séances. Ces contraintes se règlent dans les{' '}
        <Link to="/preferences?onglet=allures">préférences</Link>. Le bouton « Créer » ouvre une séance dans l'éditeur de la page{' '}
        <Link to="/seances">Séances</Link> pour la façonner à ta façon avant de l'enregistrer ; les séances que tu as
        déjà sont signalées.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 3 }}>
        <TextField
          id="vma"
          label="VMA (km/h)"
          type="number"
          value={vma}
          onChange={(e) => {
            const value = parseFloat(e.target.value);
            setVma(value);
            saveLastVma(value);
          }}
          inputProps={{ step: 0.1, min: 8, max: 30 }}
          sx={{ minWidth: 140 }}
        />
        <TextField
          id="zone"
          select
          label="Allure"
          value={zoneFiltre}
          onChange={(e) => setZoneFiltre(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value={TOUTES_LES_ZONES}>Toutes les allures</MenuItem>
          {zones.map((nom) => (
            <MenuItem key={nom} value={nom}>
              <ZoneChip nom={nom} table={table} />
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Typography sx={{ mb: 2 }}>
        <b>{seancesAffichees.length}</b> séance(s) possible(s)
        {seancesAffichees.length === 0 && " — aucune séance ne correspond à cette VMA et à ces contraintes"}.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <LegendeZones table={table} titre="Allures :" />
      </Box>

      {seancesAffichees.length > 0 && (
        <DataGrid
          rows={lignes}
          columns={columns}
          hideFooterPagination
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          sx={{ backgroundColor: 'background.paper' }}
        />
      )}
    </Box>
  );
}
